/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import images from '@assets/images/index'
import { PlayerActivityModal } from '@components/ui/PlayerActivityModal/PlayerActivityModal'
import {
  getPlayerActivityDataAndShowModal,
  playerActivityColumns,
  renderData,
  renderHeaderTooltip
} from '@utils/assessmentReportUtils'
import { generatePassword } from '@utils/utilities'
import { Flex, Progress, message } from 'antd'
import { Dispatch, SetStateAction, useMemo, useState } from 'react'

import {
  Database_Tables_Player,
  Entities_Response_Team_Player,
  LoginId,
  PlayerId,
  UserId
} from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { $SpecialGradeLevel, SpecialGradeLevel } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { useTeamInfo } from '@suntex.fim/team'
import {
  ColumnType,
  Dropdown,
  Table,
  Tooltip,
  showConfirmModal
} from '@suntex.fim/ui'
import { TrashBlue } from '@suntex.fim/ui/icons'

import { handleKeyboardClick } from '../../LearningCenter/util'
import HidableText from '../HidableText'

const DEFAULT_COLUMN_SIZE = 50
const PROGRESS_BAR_HEIGHT = 32

type PlayerData =
  | (Database_Tables_Player & Partial<Entities_Response_Team_Player>)
  | Entities_Response_Team_Player

/**
 * handles hidable columns list
 */
const hidableSelectLabel: Record<string, string> = {
  userId: 'User ID',
  displayName: 'Display Name',
  firstName: 'Name',
  password: 'Password',
  stickers: 'Stickers',
  idNumber: 'ID',
  altUser: 'Alt Login',
  altPassword: 'Alt Password',
  classificationGroup: 'Grouping'
}
/**
 * returns JSX for team roster table
 */
function TeamRosterTable({
  data,
  isLoading = true,
  graphColor,
  globalFilter,
  setFormType,
  setUserId,
  setPassword,
  setDisplayName,
  setId,
  setFirstName,
  setLastName,
  setAltLogin,
  setAltPassword,
  setLoginId,
  setAddEditStudentModal,
  getRosters,
  setIsLoading
}: {
  showModal: (t: any) => void
  data: PlayerData[]
  isLoading: boolean
  graphColor: string
  globalFilter: string
  setFormType: Dispatch<SetStateAction<'add' | 'edit'>>
  setUserId: Dispatch<SetStateAction<UserId>>
  setPassword: Dispatch<SetStateAction<string>>
  setDisplayName: Dispatch<SetStateAction<string>>
  setId: Dispatch<SetStateAction<string>>
  setFirstName: Dispatch<SetStateAction<string>>
  setLastName: Dispatch<SetStateAction<string>>
  setAltLogin: Dispatch<SetStateAction<UserId>>
  setAltPassword: Dispatch<SetStateAction<string>>
  setLoginId: Dispatch<SetStateAction<LoginId>>
  setAddEditStudentModal: Dispatch<SetStateAction<boolean>>
  getRosters: () => void
  setIsLoading: Dispatch<SetStateAction<boolean>>
}) {
  /**
   * Initialize columns list
   */
  const { api } = useFramework()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoadingPlayerActivity, setIsLoadingPlayerActivity] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [activityData, setActivityData] = useState<any>([])
  const [count, setCount] = useState<number | undefined>(0)
  const { login } = useAuth(isAuthenticated)
  const team = useTeamInfo()
  const closeModal = () => {
    setIsModalOpen(false)
  }

  const resetPlayer = async (playerId: PlayerId) => {
    setIsLoading(true)
    try {
      await api.post_Player_Reset({ playerId })
      message.success('Player reset successfully!')
      getRosters()
    } catch (error) {
      message.error('Unable to reset player.')
    }
    setIsLoading(false)
  }

  const deactivatePlayer = async (playerId: PlayerId) => {
    setIsLoading(true)
    try {
      await api.post_Player_AssignToSchool({
        playerId,
        schoolId: login.schoolId,
        grade: $SpecialGradeLevel.from(team!.grade),
        year: login.year
      })
      message.success('Player deactivate successfully!')
      getRosters()
    } catch (error) {
      message.error('Unable to deactivate player.')
    }
    setIsLoading(false)
  }
  const unassignPlayer = async (playerId: PlayerId) => {
    setIsLoading(true)
    try {
      await api.post_Player_AssignToUnassignedTeam({
        playerId,
        schoolId: login.schoolId,
        grade: team!.grade
      })
      message.success('Player move to unassigned successfully!')
      getRosters()
    } catch (error) {
      message.error('Unable to move to unassigned.')
    }
    setIsLoading(false)
  }
  const generateUserId = async (loginId: LoginId) => {
    setIsLoading(true)
    try {
      await api.post_Team_GenerateNewUserId({
        loginId
      })
      message.success('New userId was generated for this player!')
      getRosters()
    } catch (error) {
      message.error('Unable to generate new userId for this player.')
    }
    setIsLoading(false)
  }
  const moveTo = async (playerId: PlayerId) => {
    setIsLoading(true)
    try {
      await api.post_Player_UpdatePrimaryTeam({
        playerId,
        teamId: team!.id
      })
      message.success('Player moved to this team !')
      getRosters()
    } catch (error) {
      message.error('Unable to move player to this team.')
    }
    setIsLoading(false)
  }

  function confirmPlayerAction(
    playerId: PlayerId | undefined,
    action: 'reset' | 'deactivate' | 'unassign' | 'move'
  ) {
    if (playerId !== undefined) {
      showConfirmModal({
        content: `Are you SURE you want to ${action} this student? This action is permanent.`,
        icon: null, // This removes the exclamation icon
        // closable: true,
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          if (action === 'reset') {
            await resetPlayer(playerId)
          } else if (action === 'deactivate') {
            await deactivatePlayer(playerId)
          } else if (action === 'unassign') {
            await unassignPlayer(playerId)
          } else if (action === 'move') {
            await moveTo(playerId)
          }
        }
      })
    }
  }

  function confirmLoginAction(
    loginId: LoginId | undefined,
    action: 'generate new password for' | 'generate new user id for'
  ) {
    if (loginId !== undefined) {
      showConfirmModal({
        content: ` Are you SURE you want to ${action.toLocaleLowerCase()} this student?`,
        icon: null, // This removes the exclamation icon
        // closable: true,
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          if (action === 'generate new password for') {
            await generatePassword(api, loginId)
            message.success('New password generated for this student!')
            getRosters()
          } else if (action === 'generate new user id for') {
            await generateUserId(loginId)
            message.success('New user ID generated for this student!')
          }
        }
      })
    }
  }

  const columnKeys = [
    'userId',
    'displayName',
    'firstName',
    'password',
    'stickers',
    'idNumber',
    'altUser',
    'altPassword',
    'classificationGroup'
  ]

  /**
   * renders progress bar in table cell
   */
  const levelColumns = useMemo<ColumnType<PlayerData>[]>(
    () =>
      columnKeys.map((key) => ({
        header: hidableSelectLabel[key],
        accessorKey: key,
        id: key,
        size: 90,
        cell: (info) => (
          <Tooltip
            title={`${Number(info.getValue()) || 0}%`}
            classNames={{ root: 'progress-tooltip' }}
            arrow={false}
          >
            <Progress
              strokeColor={graphColor}
              percent={Number(info.getValue()) || 0}
              size={['100%', PROGRESS_BAR_HEIGHT]}
              showInfo={false}
            />
          </Tooltip>
        )
      })),
    [columnKeys]
  )

  /**
   * handles table column mapping
   */
  const columns = useMemo<ColumnType<PlayerData>[]>(
    () => [
      {
        accessorKey: 'action',
        id: 'actions',
        size: DEFAULT_COLUMN_SIZE,
        enableSorting: false,
        header: (header) => {
          return (
            <Dropdown
              ariaLabel="Select visible columns"
              icon={null}
              menu={{
                items: header.table
                  .getAllFlatColumns()
                  .filter((column) =>
                    Object.keys(hidableSelectLabel).includes(column.id)
                  )
                  .map((column) => ({
                    key: column.id,
                    label: (
                      <div
                        key={column.id}
                        className="px-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <label htmlFor={column.id}>
                          <div className="pretty p-icon p-toggle">
                            <input
                              {...{
                                id: column.id,
                                type: 'checkbox',
                                checked: column.getIsVisible(),
                                onChange: column.getToggleVisibilityHandler()
                              }}
                            />
                            <div className="state p-on">
                              <images.CheckboxCheckedSm />
                            </div>
                            <div className="state p-off">
                              <images.CheckboxSm />
                            </div>
                          </div>{' '}
                          <span>{hidableSelectLabel[column.id]}</span>
                        </label>
                      </div>
                    )
                  }))
              }}
              trigger={['click']}
              className="table-header-icon"
              overlayClassName="table-header-icon"
            >
              <div>
                <images.SettingTable />
              </div>
            </Dropdown>
          )
        },
        cell: (cell) => {
          return (
            <Dropdown
              ariaLabel="Open student actions menu"
              icon={null}
              menu={{
                items: [
                  {
                    key: '1',
                    label: 'Edit Student',
                    icon: <images.Edit />,
                    onClick: () => {
                      setFormType('edit')
                      setUserId(cell.row.original.userId || ('' as UserId))
                      setPassword(cell.row.original.password || '')
                      setDisplayName(cell.row.original.displayName || '')
                      setId(cell.row.original.idNumber || '')
                      setFirstName(cell.row.original.firstName || '')
                      setLastName(cell.row.original.lastName || '')
                      setAltLogin(cell.row.original.altUser || ('' as UserId))
                      setAltPassword(cell.row.original.altPassword || '')
                      setLoginId(cell.row.original.loginId || (0 as LoginId))
                      setAddEditStudentModal(true)
                    }
                  },
                  {
                    key: '2',
                    label: 'Player Activity',
                    icon: <images.FlashCircle />,
                    onClick: () =>
                      getPlayerActivityDataAndShowModal(
                        api,
                        cell.row.original.playerId!,
                        setIsModalOpen,
                        setIsLoadingPlayerActivity,
                        setActivityData,
                        setCount
                      )
                  },
                  {
                    key: '3',
                    label: 'Generate a new user id for this student',
                    icon: <images.RefreshSquare />,
                    onClick: () =>
                      confirmLoginAction(
                        cell.row.original.loginId,
                        'generate new user id for'
                      )
                  },
                  {
                    key: '4',
                    label: 'Generate a new password for this student',
                    icon: <images.RefreshSquare />,
                    itemIcon: <images.HelpCircle />,
                    onClick: () =>
                      confirmLoginAction(
                        cell.row.original.loginId,
                        'generate new password for'
                      )
                  },
                  {
                    key: '5',
                    label: 'Remove student from team and move to unassigned',
                    icon: <TrashBlue />,
                    onClick: () =>
                      confirmPlayerAction(
                        cell.row.original.playerId,
                        'unassign'
                      )
                  },
                  {
                    key: '6',
                    label: 'Deactivate this student',
                    icon: <images.Slash />,
                    itemIcon: <images.HelpCircle />,
                    onClick: () =>
                      confirmPlayerAction(
                        cell.row.original.playerId,
                        'deactivate'
                      )
                  },
                  {
                    key: '7',
                    label: 'Move a student from another team to this one',
                    icon: <images.Slash />,
                    itemIcon: <images.HelpCircle />,
                    disabled: cell.row.original.isPrimaryTeam,
                    onClick: () =>
                      confirmPlayerAction(cell.row.original.playerId, 'move')
                  },
                  {
                    key: '8',
                    label: 'Reset this student',
                    icon: <images.RotateDevice />,
                    itemIcon: <images.HelpCircle />,
                    onClick: () =>
                      confirmPlayerAction(cell.row.original.playerId, 'reset')
                  }
                ]
              }}
              trigger={['click']}
              className="table-cell-icon"
              overlayClassName="table-cell-icon table-cell-droppdown-icon"
            >
              <div>
                <images.SquareMenu />
              </div>
            </Dropdown>
          )
        }
      },
      {
        id: 'userId',
        accessorKey: 'userId',
        header: () => renderHeaderTooltip('User ID'),
        size: DEFAULT_COLUMN_SIZE,
        cell: (info) => {
          const userId = info.getValue() as string
          const message: string = info.row.original.isPrimaryTeam
            ? 'This team is the primary team for this player.'
            : 'This player is a guest on this team.'

          return (
            <Tooltip
              arrow={false}
              classNames={{ root: 'progress-tooltip' }}
              title={message}
            >
              <span
                tabIndex={0}
                role="button"
                aria-label={`User ID ${userId}. ${message}`}
                onKeyDown={(e) => {
                  handleKeyboardClick(e)
                }}
              >
                {userId}
              </span>
            </Tooltip>
          )
        }
      },
      {
        header: () => renderHeaderTooltip('Display Name'),
        size: DEFAULT_COLUMN_SIZE,
        accessorKey: 'displayName',
        cell: (info) => {
          return <b>{renderData(info.getValue<string>())}</b>
        },
        id: 'displayName'
      },
      {
        header: () => renderHeaderTooltip('Name'),
        size: DEFAULT_COLUMN_SIZE,
        accessorKey: 'firstName',
        cell: (info) => {
          const player = info.row.original
          return (
            <Flex align="center" justify="center">
              <b>{player.firstName || 'No first name'}</b>
              <HidableText
                text={player.lastName || 'No last name'}
                hidableText={
                  player.lastName?.substring(0, 3) ||
                  'No last name'.substring(0, 3)
                }
              />
            </Flex>
          )
        },
        id: 'firstName',
        enableHiding: true
      },
      {
        header: () => renderHeaderTooltip('Password'),
        accessorKey: 'password',
        size: DEFAULT_COLUMN_SIZE,
        cell: (info) => (
          <HidableText
            text={info.getValue<string>()}
            hidableText={
              info.getValue<string>()?.substring(0, 4) ||
              'No password'.substring(0, 4)
            }
          />
        ),
        id: 'password',
        enableHiding: true,
        enableGlobalFilter: false
      },
      {
        header: () => renderHeaderTooltip('Stickers'),
        accessorKey: 'stickers',
        size: DEFAULT_COLUMN_SIZE,
        cell: (info) => <b>{info.getValue<string>().toLocaleString()}</b>,
        id: 'stickers',
        enableHiding: true
      },
      {
        header: () => renderHeaderTooltip('ID'),
        size: DEFAULT_COLUMN_SIZE,
        accessorFn: ({ idNumber }) => {
          return idNumber || ''
        },
        cell: (info) => <b>{renderData(info.getValue<string>())}</b>,
        id: 'idNumber',
        enableHiding: true
      },
      {
        header: () => renderHeaderTooltip('Alt Login'),
        size: DEFAULT_COLUMN_SIZE,
        accessorKey: 'altUser',
        cell: (info) => <b>{renderData(info.getValue<string>())}</b>,
        id: 'altUser',
        enableHiding: true,
        enableGlobalFilter: true
      },
      {
        header: () => renderHeaderTooltip('Alt Password'),
        size: DEFAULT_COLUMN_SIZE,
        accessorKey: 'altPassword',
        cell: (info) => (
          <HidableText
            text={info.getValue<string>() || 'No password'}
            hidableText={
              info.getValue<string>()?.substring(0, 4) ||
              'No password'.substring(0, 4)
            }
          />
        ),
        id: 'altPassword',
        enableHiding: true,
        enableGlobalFilter: true
      }
    ],
    [levelColumns]
  )

  return (
    <>
      <Table
        data={data}
        isLoading={isLoading}
        globalFilter={globalFilter}
        columns={columns}
        initialSorting={[{ id: 'userId', desc: false }]}
      />
      <PlayerActivityModal
        isModalOpen={isModalOpen}
        handleOk={closeModal}
        handleCancel={closeModal}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        dataSource={activityData}
        columns={playerActivityColumns}
        count={count}
        isLoading={isLoadingPlayerActivity}
      />
    </>
  )
}

export default TeamRosterTable
